import { X } from 'lucide-react';
import { type ComponentPropsWithoutRef, forwardRef, type MouseEvent } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import { Stage } from '@/components/Stage/Stage';
import {
  default as Tooltip,
  default as TooltipV2,
} from '@/components/Tooltip/TooltipV2';
import { cn } from '@/lib/cn';
import type { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import FieldItemOperatorSelector from './FieldItemOperatorSelector';

type Props = {
  filterIndex: number;
  isMultiValueField: boolean;
  columnName: string;
  parentFieldName?: string;
  filterQuery: StructuredFilterQueryFormatT;
  onOperatorChange?: (operator: STRUCTURED_FILTER_OPERATOR) => void;
  onClickValue?: (e: MouseEvent<HTMLDivElement>) => void;
  onRemoveClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

const FieldItemForInsight = (props: Props) => {
  const labelString = props.filterQuery.filterValues
    .map((value) => value.filterName)
    .join(', ');
  const visibleLabelString = props.filterQuery.filterValues
    .map((value) => value.filterName)
    .slice(0, 3)
    .join(', ');

  return (
    <Style>
      <SectionWrapper style={{ paddingLeft: 12 }}>
        <TooltipV2
          tooltipNode={
            props.parentFieldName &&
            `${props.parentFieldName} : ${props.columnName}`
          }
        >
          <div className="text-[13px] font-medium">{props.columnName}</div>
        </TooltipV2>
      </SectionWrapper>
      {!props.filterQuery.rangeValues && (
        <SectionWrapper>
          <FieldItemOperatorSelector
            dropdownKey={`filter-item-operator-selector-${props.filterQuery.columnKey}-${props.filterIndex}`}
            operator={props.filterQuery.operator}
            hasMultiValues={props.filterQuery.filterValues.length > 1}
            isMultiValueField={props.isMultiValueField}
            onChange={
              props.onOperatorChange
                ? (v) => props.onOperatorChange?.(v)
                : undefined
            }
          />
        </SectionWrapper>
      )}
      <Tooltip tooltipNode={labelString}>
        <SectionWrapper style={{ cursor: 'pointer' }}>
          <Stage>
            <Stage.Scene
              status={!!props.filterQuery.rangeValues}
              data={props.filterQuery.rangeValues}
            >
              {(rangeValue) => {
                return (
                  <div
                    className="text-sm text-ellipsis max-w-400"
                    onClick={props.onClickValue}
                  >
                    {Number.isNaN(rangeValue.min) ? '' : rangeValue.min} ~{' '}
                    {Number.isNaN(rangeValue.max) ? '' : rangeValue.max}
                  </div>
                );
              }}
            </Stage.Scene>
            <Stage.Scene default>
              <div
                className="max-w-[400px] truncate text-[13px] font-medium"
                onClick={props.onClickValue}
              >
                {visibleLabelString}
                {props.filterQuery.filterValues.length > 3 && '...'}
              </div>
            </Stage.Scene>
          </Stage>
        </SectionWrapper>
      </Tooltip>
      <SectionWrapper
        style={{
          cursor: 'pointer',
          paddingRight: 8,
        }}
        onClick={props.onRemoveClick}
      >
        <X className="size-3 text-gray-600" />
      </SectionWrapper>
    </Style>
  );
};

const Style = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: 16px;
  height: 28px;
  overflow: hidden;
  align-items: stretch;
  background-color: ${({ theme }) => theme.colors.gray100};
`;

type SectionWrapperProps = ComponentPropsWithoutRef<'div'>;

const SectionWrapper = forwardRef<HTMLDivElement, SectionWrapperProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center px-1 transition-all duration-200 hover:bg-muted',
          className,
        )}
        {...rest}
      />
    );
  },
);

export default FieldItemForInsight;
